import { useEffect, useRef } from 'react'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { mapFlexibleDatesToTypeaheadFormat } from 'lib/search/searchUtils'
import { isValidOccupancy } from 'lib/offer/occupancyUtils'
import usePersistTripChatRecentSearch from 'tripPlanner/hooks/usePersistTripChatRecentSearch'
import type { UpsertTripChatRecentSearchBody } from 'tripPlanner/utils/tripChatRecentSearchPayload'

interface Options {
  chatId?: number
  tripStopId?: number
  searchQuery?: string
  /** Only present when an edited carousel actually starts a hotel search. */
  filters?: App.OfferListFilters
}

export default function usePersistCarouselHotelSearch({ chatId, tripStopId, searchQuery, filters }: Options) {
  const persist = usePersistTripChatRecentSearch(chatId)
  const lastSavedKey = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!chatId || !searchQuery || !filters || !filters.rooms || !isValidOccupancy(...filters.rooms)) return

    const body: UpsertTripChatRecentSearchBody = {
      surface: 'hotels',
      tripStopId: tripStopId ?? null,
      payload: {
        location: {
          query: searchQuery,
          // The recent-search contract's placeId represents a destination. Don't
          // restore a landmark id as a destination in the hotel drawer.
          ...(filters.destinationId ? { placeId: filters.destinationId } : {}),
        },
        dateSelection: filters.checkIn && filters.checkOut
          ? { type: 'specific', checkIn: filters.checkIn, checkOut: filters.checkOut }
          : filters.flexibleMonths && filters.flexibleNights
            ? mapFlexibleDatesToTypeaheadFormat(filters.flexibleMonths, filters.flexibleNights as FLEXIBLE_DURATION_RANGE)
            : { type: 'anytime' },
        occupancies: filters.rooms,
      },
    }
    const key = JSON.stringify({ chatId, body })
    if (lastSavedKey.current === key) return
    lastSavedKey.current = key
    // Save at search start, like the drawer. A slow response from an older filter
    // selection must not overwrite the settings of a more recent search.
    persist(body)
  }, [chatId, filters, persist, searchQuery, tripStopId])
}
